import cx from "classnames";

import { IPatient } from "common/types";
import { Optional } from "common/utils/types";
import Image from "common/components/Image";

import SelectPanel from "client/common/components/inputs/SelectPanel";
import { RenderContentProps } from "client/common/components/buttons/SelectButton";

import { imageForSpecies } from "../utils";

import styles from "../styles/PatientSelectPanel.module.less";

const noop = () => null;

const getSplitImageSrcs = (household: IPatient[]) => {
  const maxImages = 2;
  return household.slice(0, maxImages).map((patient) => ({
    id: String(patient.id),
    src: imageForSpecies(patient.species, false),
  }));
};

type RequiredAddProps = { hasAdd: true; onAdd: () => void } | { hasAdd?: false };

export type MultiPatient = { multi: true };

export type Option = IPatient | MultiPatient;

export const isPatient = (option: Option): option is IPatient => {
  return !(option as MultiPatient).multi;
};

type PatientSelectProps = {
  options: Option[];
  splitPatients?: IPatient[];
  value: Optional<Option>;
  onSelect: (value: IPatient) => void;
  onSelectMulti?: () => void;
  hasAdd?: boolean;
  onAdd?: () => void;
  loadingValue?: Optional<Option>;
  disabled?: boolean;
  renderTag?: (value: Option) => React.ReactNode;
} & RequiredAddProps;

const PatientSelectPanel = ({
  options,
  splitPatients,
  value,
  onSelect,
  onSelectMulti = noop,
  hasAdd = false,
  onAdd = noop,
  loadingValue,
  disabled = false,
  renderTag = noop,
}: PatientSelectProps) => {
  const getKey = (option: Optional<Option>) => {
    if (!option) {
      return "";
    }
    return isPatient(option) ? option.id : "multi";
  };

  const household = splitPatients ?? options.filter(isPatient);

  const renderOption = (option: Option, { isActive }: RenderContentProps) => {
    const imageSrcs = isPatient(option)
      ? [{ id: String(option.id), src: imageForSpecies(option.species, isActive) }]
      : getSplitImageSrcs(household);
    return (
      <>
        <div className={styles.optionContent}>
          <span className={cx(styles.petName, !isPatient(option) && styles.multi)}>
            {isPatient(option) ? option.name_display : "Split between pets"}
          </span>
          <div className={styles.species}>
            {imageSrcs.map((image) => (
              <Image
                key={image.id}
                src={image.src}
                alt={isPatient(option) ? option.species_display : "Multiple pets"}
              />
            ))}
          </div>
          {renderTag(option)}
        </div>
      </>
    );
  };

  const optionsWithPin =
    value &&
    isPatient(value) &&
    !options.some((option) => isPatient(option) && option.id === value.id)
      ? [value, ...options]
      : options;

  return (
    <SelectPanel<Option>
      variant="horizontal"
      entityName="Pet"
      getKey={getKey}
      getState={(option) =>
        isPatient(option)
          ? {
              isLoading:
                !!loadingValue && isPatient(loadingValue) && loadingValue?.id === option.id,
              disabled,
            }
          : { isLoading: false, disabled: false }
      }
      options={optionsWithPin}
      value={value}
      onClick={(option: Option) => (isPatient(option) ? onSelect(option) : onSelectMulti())}
      hasAdd={hasAdd}
      onAdd={onAdd}
      renderAdd={({ icon }) => (
        <div className={styles.addPet}>
          {icon}
          Add new pet
        </div>
      )}
      renderOption={renderOption}
    />
  );
};

export default PatientSelectPanel;
